import { ORGANIZATION_TAB_TYPES } from '~/pages/Organization/utils.tsx';

export const PORTAL_BASE_SLUG = '/portals';

export enum OrgDetailsDataType {
  OVERVIEW = 'overview',
  SSO_CONNECTIONS = 'sso',
  DIRECTORIES = 'scim',
  USERS = 'users',
  SECURITY = 'security',
  ROLES = 'roles'
}

export enum DirectoryDetailsTabTypes {
  CONFIGURATION = 'configuration',
  EVENTS = 'events',
  USERS = 'users',
  GROUPS = 'groups'
}

export enum ConnectionDetailsTabTypes {
  CONFIGURATION = 'configuration',
  SETTINGS = 'settings'
}

export enum CUSTOMER_PORTAL_BASE_PATHS {
  UNKNOWN = '/:portalType',
  SSO = `/${OrgDetailsDataType.SSO_CONNECTIONS}`,
  DIRECTORY_SYNC = `/${OrgDetailsDataType.DIRECTORIES}`
}

enum AppBaseNamespaces {
  WORKSPACE = '/ws',
  CUSTOMER_PORTAL = PORTAL_BASE_SLUG,
  CUSTOMER_PORTAL_SSO = `${PORTAL_BASE_SLUG}/:portalType`,
  HANDLERS = '/ui-handlers',
  HOSTED_AUTH_PAGES = '/a',
  ENV_APP_PAGES = '/ui'
}

enum PublicPagePath {
  LOGIN = '/login',
  SIGNUP = '/signup'
}
enum EnvironmentPagePath {
  ENVIROMNEMT_GETTING_STARTED = '/getting-started',
  ENVIROMNEMT_QUICK_START = '/quick-start',
  ENVIROMNEMT_QUICK_START_MODULE = '/quick-start/:moduleType',
  ENVIROMNEMT_ORGANIZATIONS = '/organizations',
  ENVIROMNEMT_USERS = '/users',
  ENVIROMNEMT_USER_DETAILS = '/users/:userId',
  ENVIRONMENT_ORGANIZATION_DETAILS_OVERVIEW = '/organizations/:organizationId/:tabType',
  ENVIRONMENT_ORGANIZATION_DETAILS_CONNECTION_DETAILS = `/organizations/:organizationId/${ORGANIZATION_TAB_TYPES.SSO_CONNECTIONS}/:connectionId/:connectionTabType`,
  ENVIRONMENT_ORGANIZATION_DETAILS_DIRECTORY_DETAILS = `/organizations/:organizationId/${ORGANIZATION_TAB_TYPES.DIRECTORY_SYNC}/:directoryId/:directoryTabType`,
  ENVIRONMENT_ORGANIZATION_ADD_ROLES = `/organizations/:organizationId/${ORGANIZATION_TAB_TYPES.ROLES}/add`,
  ENVIRONMENT_ORGANIZATION_ROLE_DETAILS = `/organizations/:organizationId/${ORGANIZATION_TAB_TYPES.ROLES}/:roleName`,
  ENVIROMNEMT_ORGANIZATION_DETAILS_USER_DETAILS = '/organizations/:organizationId/users/:userId',
  ENVIRONMENT_AUTHENTICATION = '/authentication',
  ENVIRONMENT_AUTHENTICATION_TABS = '/authentication/:tabType',
  ENVIRONMENT_AUTHENTICATION_METHODS = '/authentication/:tabType/:authMethod',
  ENVIROMNEMT_AUDIT_LOGS = '/audit-logs',
  ENVIRONMENT_API_CREDENTIALS = '/api-credentials',
  ENVIRONMENT_SINGLE_SIGN_ON = '/sso/:tabType',
  ENVIRONMENT_DIRECTORY_SYNC = '/scim/:tabType',
  ENVIRONMENT_SOCIAL_CONNECTIONS = '/connections/social',
  ENVIRONMENT_SOCIAL_CONNECTION_DETAILS = '/connections/social/:connectionId',
  ENVIRONMENT_SOCIAL_LOGIN_DETAILS = '/authentication/:tabType/:authMethod/:connectionId',
  // Old Customization Nav Item Routes
  ENVIRONMENT_CUSTOMIZATIONS = '/customizations',
  ENVIRONMENT_CUSTOMIZATION_TABS = '/customizations/:tabType',
  // New Customization Nav Item Routes
  ENVIRONMENT_BRANDING = '/branding',
  ENVIRONMENT_BRANDING_BY_WORKFLOW_TYPE = '/branding/:workflowType',
  ENVIRONMENT_WEBHOOKS = '/webhooks',
  ENVIRONMENT_WEBHOOK_DETAILS = '/webhooks/:id',
  ENVIRONMENT_ROLES = '/roles',
  ENVIRONMENT_USER_MANAGEMENT = '/user-management',
  ENVIRONMENT_USER_MANAGEMENT_TABS = '/user-management/:tabType',
  ENVIRONMENT_USER_ATTRIBUTES = '/user-attributes',
  EMAIL_CONFIGURATIONS = '/emails/configure',
  ENVIRONMENT_AUTHORIZATION = '/authorization',
  ENVIRONMENT_AUTHORIZATION_TABS = '/authorization/:tabType',
  ENVIRONMENT_ADD_ROLE = '/authorization/:tabType/add',
  ENVIRONMENT_ROLE_DETAILS = '/authorization/:tabType/:roleName',
  ENVIRONMENT_MCP_SERVERS_LIST = '/mcp-servers',
  ENVIRONMENT_ADD_MCP_SERVER = '/mcp-servers/add',
  ENVIRONMENT_MCP_SERVER_DETAILS = '/mcp-servers/:serverId/:tabType',
  SCALEKIT_CONNECT_INTEGRATIONS = '/connect',
  SCALEKIT_CONNECT_INTEGRATION_DETAILS = '/connect/integrations/:connectionId/:tabType',
  SCALEKIT_CONNECT_TOOL_EXECUTION_PLAYGROUND = '/connect-playground',
  SCALEKIT_CONNECTED_PROVIDERS = '/connect/providers',
  SCALEKIT_CONNECTED_ACCOUNTS = '/connected-accounts',
  ENVIRONMENT_SETTINGS = '/settings',
  ENVIRONMENT_SETTINGS_TABS = '/settings/:tabType',
  ENVIRONMENT_AUTHENTICATION_LOGS = '/auth-logs',
  ENVIRONMENT_AUTH_LOGS_EVENT_PAYLOAD = '/auth-logs/:authRequestId/events/:eventId/payload',
  ENVIRONMENT_EMAILS = '/emails',
  ENVIRONMENT_EMAILS_TABS = '/emails/:tabType',
  ENVIRONMENT_EMAILS_TEMPLATE_DETAILS = `/emails/:tabType/:useCase`,
  ENVIRONMENT_CUSTOM_DOMAIN = '/custom-domain',
  ENVIRONMENT_INTERCEPTORS = '/interceptors',
  ENVIRONMENT_ADD_INTERCEPTORS = '/interceptors/add',
  ENVIRONMENT_INTERCEPTOR_DETAILS = '/interceptors/:interceptorId/:tabType'
}
enum WorkspacePagePath {
  WORKSPACE_ONBOARDING = '/onboarding',
  WORKSPACE_GENERAL = '/settings/general',
  WORKSPACE_MEMBERS = '/members',
  WORKSPACE_MANAGE_ENVIRONMENTS = '/manage/environments',
  WORKSPACE_BILLING_V1 = '/billing-v1',
  WORKSPACE_BILLING = '/billing/:tabType'
}

enum MyAccountPagePath {
  MYACCOUNT_PROFILE = '/profile',
  MYACCOUNT_PREFERENCES = '/preferences',
  MYACCOUNT_NOTIFICATIONS = '/notifications'
}

enum CustomerPortalPath {
  LIST_CONNECTIONS = '/organizations/:organizationId/connections',
  CONNECTION_OVERVIEW = '/organizations/:organizationId/connections/:connectionId',
  CONNECTION_DETAILS = '/organizations/:organizationId/connections/:connectionId/:connectionTabType',
  CONNECTION_DOC = '/organizations/:organizationId/connections/:connectionId/docs',
  LIST_DIRECTORIES = '/organizations/:organizationId/directories',
  DIRECTORY_DETAILS = '/organizations/:organizationId/directories/:directoryId/:directoryTabType'
}

enum HandlersPath {
  TEST_SSO = '/sso/:connectionId/test',
  LOGIN_CALLBACK = '/auth/callback',
  DIRECTORY_SYNC_CALLBACK = 'directory/:directoryId/callback',
  CROSS_BROWSER_AUTH_FAILURE = '/cross-browser/restricted',
  CONNECT_AUTH_SUCCESS = '/connect/callback/success'
}

enum HostedAuthPaths {
  LOGIN = '/auth/login',
  LOGIN_PREVIEW = '/auth/login/preview/:previewId',
  ADMIN_PORTAL_PREVIEW = '/auth/admin-portal/preview/:previewId',
  SIGNUP = '/auth/signup',
  ORG_SWITCHER = '/auth/switcher',
  GENERIC_ERROR = '/auth/error',
  GENERIC_ERROR_ALIAS = '/auth/login/error', // @todo remove this path before merging
  OAUTH_CONSENT = '/auth/oauth/consent',
  CONFIRM_LOGOUT = '/auth/logout/confirm',
  LOGOUT_SUCCESS = '/auth/logout/success'
}

enum EnvAppPaths {
  PASSKEYS = '/profile/passkeys',
  USER_PROFILE = '/profile'
}

export type AnyAppPath =
  | AppBaseNamespaces
  | PublicPagePath
  | EnvironmentPagePath
  | WorkspacePagePath
  | MyAccountPagePath
  | CustomerPortalPath
  | HandlersPath
  | HostedAuthPaths
  | EnvAppPaths;

function shouldIgnoreUnauthorizedLoginRedirection(): boolean {
  // Add all public page routes here to keep the unprotected
  // Else non-logged in users will be redirected to /login for those routes
  const publicPaths: Set<string> = new Set([
    getSlug.publicPage(PublicPagePath.LOGIN, true),
    getSlug.publicPage(PublicPagePath.SIGNUP, true)
  ]);
  return publicPaths.has(window.location.pathname);
}

const replacePathParams = (
  path: AnyAppPath,
  pathParams: Record<string, any> = {}
): string => {
  return Object.keys(pathParams).reduce(
    (currentPath: string, currentParam: string) => {
      return pathParams[currentParam] !== undefined
        ? currentPath.replace(`:${currentParam}`, pathParams[currentParam])
        : currentPath;
    },
    path
  );
};

const getFullPath = (
  path: string,
  includeNamespace: boolean = false,
  nameSpace: AppBaseNamespaces | string
) => (includeNamespace ? `${nameSpace}${path}` : path);

const getSlug = {
  workspaceBaseNamespace: () => AppBaseNamespaces.WORKSPACE,
  customerPortalBaseNamespace: () => AppBaseNamespaces.CUSTOMER_PORTAL,
  customerPortalSSOBaseNamespace: () =>
    `${AppBaseNamespaces.CUSTOMER_PORTAL}${CUSTOMER_PORTAL_BASE_PATHS.SSO}`,
  customerPortalDirSyncBaseNamespace: () =>
    `${AppBaseNamespaces.CUSTOMER_PORTAL}${CUSTOMER_PORTAL_BASE_PATHS.DIRECTORY_SYNC}`,
  getHandlersBaseNamespace: () => AppBaseNamespaces.HANDLERS,
  getHostedAuthPagesBaseNamespace: () => AppBaseNamespaces.HOSTED_AUTH_PAGES,
  getEnvAppPagesBaseNamespace: () => AppBaseNamespaces.ENV_APP_PAGES,
  publicPage: (path: PublicPagePath, includeNamespace?: boolean) =>
    getFullPath(`${path}`, includeNamespace, AppBaseNamespaces.WORKSPACE),
  environmentById: (
    envId?: string,
    path?: EnvironmentPagePath,
    includeNamespace?: boolean
  ) => {
    const environmentId: string = envId || ':environmentId';
    return getFullPath(
      `/environments/${environmentId}${path ? path : ''}`,
      includeNamespace,
      AppBaseNamespaces.WORKSPACE
    );
  },
  workspace: (path: WorkspacePagePath, includeNamespace?: boolean) =>
    getFullPath(
      `/workspace${path}`,
      includeNamespace,
      AppBaseNamespaces.WORKSPACE
    ),
  myAccount: (path: MyAccountPagePath, includeNamespace?: boolean) =>
    getFullPath(`/my${path}`, includeNamespace, AppBaseNamespaces.WORKSPACE),
  portalLink: (
    organizationId?: string,
    path: string = CustomerPortalPath.LIST_CONNECTIONS,
    portalTypeBase: CUSTOMER_PORTAL_BASE_PATHS = CUSTOMER_PORTAL_BASE_PATHS.UNKNOWN,
    includeNamespace?: boolean,
    pathParams: Record<string, any> = {}
  ): string => {
    const namespacePrefix = includeNamespace ? '' : portalTypeBase;
    let resourceTypeBase = '';

    if (portalTypeBase === CUSTOMER_PORTAL_BASE_PATHS.SSO) {
      resourceTypeBase = getSlug.customerPortalSSOBaseNamespace();
    } else if (portalTypeBase === CUSTOMER_PORTAL_BASE_PATHS.DIRECTORY_SYNC) {
      resourceTypeBase = getSlug.customerPortalDirSyncBaseNamespace();
    }

    const pathFragment = getFullPath(
      replacePathParams(path as AnyAppPath, {
        organizationId,
        ...pathParams
      }),
      includeNamespace,
      resourceTypeBase
    );

    return `${namespacePrefix}${pathFragment}`;
  },
  handlers: (
    path: string,
    includeNamespace?: boolean,
    pathParams: Record<string, any> = {}
  ): string => {
    return getFullPath(
      replacePathParams(path as AnyAppPath, pathParams),
      includeNamespace,
      AppBaseNamespaces.HANDLERS
    );
  },
  hostedAuthPages: (
    path: string,
    includeNamespace?: boolean,
    pathParams: Record<string, any> = {}
  ): string => {
    return getFullPath(
      replacePathParams(path as AnyAppPath, pathParams),
      includeNamespace,
      AppBaseNamespaces.HOSTED_AUTH_PAGES
    );
  },
  envAppPages: (
    path: string,
    includeNamespace?: boolean,
    pathParams: Record<string, any> = {}
  ): string => {
    return getFullPath(
      replacePathParams(path as AnyAppPath, pathParams),
      includeNamespace,
      AppBaseNamespaces.ENV_APP_PAGES
    );
  }
};

export {
  AppBaseNamespaces,
  PublicPagePath,
  EnvironmentPagePath,
  WorkspacePagePath,
  MyAccountPagePath,
  CustomerPortalPath,
  HandlersPath,
  HostedAuthPaths,
  EnvAppPaths,
  shouldIgnoreUnauthorizedLoginRedirection,
  replacePathParams,
  getSlug
};
